/**
 * @file Names the teams the Overview shows, each a single-document listener on
 * `businessGroups/{id}`, which every signed-in user may read. Consumers: the
 * Overview panel and its tables, the level titles, the Metrics and Projects
 * panels and Home. A team is its business group `name`, else its id, so a
 * marked id with no group still shows; a null team is "No team". Callers join
 * a person's several teams themselves.
 */
import type { BusinessGroup } from '#types';
import { useStoreDocsByPaths } from '@/store';

const pathOf = (teamId: string): string => `businessGroups/${teamId}`;

export const useAnalyticsTeamNames = (
  teamIds: readonly string[],
): ((teamId: string | null) => string) => {
  const paths = [...new Set(teamIds.filter((teamId) => teamId !== ''))]
    .sort()
    .map(pathOf);
  const { data } = useStoreDocsByPaths<BusinessGroup>(paths);
  return (teamId) =>
    teamId === null ? 'No team' : data.get(pathOf(teamId))?.name || teamId;
};
